import React, { useCallback, useEffect, useState } from 'react';
import CapabilityMatrix from '../components/CapabilityMatrix';
import ExportButton from '../features/reports/ExportButton';
import './MissionControlPage.css';

// ONE view, no tabs (David 2026-08-09). Last Week and Last 3 Months compared a practice
// against its own past; this grid compares practices against each other, so the axis of
// comparison is the rows. Each measure now carries its own period server-side and the label
// under the heading states them, since Claims covers 30 days while Rev / Paid Claim covers 60.
const PERIOD = 'current';

// David's five denial causes. One server-side reason covers all five, because the blocker is
// the same for every bucket: nobody has yet defined what counts as a denial for a practice
// whose claim statuses and whose remittance reason codes disagree.
const DENIAL_BUCKETS = ['Eligibility', 'Coding', 'Credentialing', 'PreCert', 'Other'];

// Aged A/R is a point-in-time stock, so the server sends it only for a block carrying an
// as-of date and the cell reads n/a otherwise.
//
// EVERY column David drew, but grouped: the six that carry a figure first, then the eight
// that do not. Within each group the order is his, from the layout he sent on 2026-08-03, so
// the grid still reads against his sheet.
//
// Grouping is what makes the empty cells safe to render as a plain dash. A dash on its own
// cannot distinguish "not built" from "no value this period", and an absence that reads like
// a value is how three columns shipped wrong in session 19 — but a reader who can see that
// the whole right-hand block is empty does not need the cell to say so. The reason still
// rides the cell on hover, and the headings there are muted.
const COLUMNS = [
  { key: 'claims_per_appointment', label: 'Claims / Appt', kind: 'ratio' },
  { key: 'revenue_per_paid_claim', label: 'Rev / Paid Claim', kind: 'money' },
  { key: 'aged_ar_45_plus', label: '45+ A/R', kind: 'ar' },
  // Beside the figure it describes, deliberately. A stock measure like the A/R total, so the
  // server sends it only for the block carrying an as-of date and it reads n/a elsewhere.
  { key: 'aged_ar_45_plus_days', label: '45+ A/R Days', kind: 'days' },
  { key: 'new_clients', label: 'New Clients', kind: 'count' },
  { key: 'claims', label: 'Claims', kind: 'count' },
  { key: 'appts', label: 'Appts', kind: 'count' },
  // % ERAs REMOVED from the grid on David's call 2026-08-09 — the figure is wrong and the
  // column is out until it is reworked. The server still computes and sends `pct_eras`, so
  // putting it back is one line here; nothing downstream was deleted.
  { key: 'mtd_collections', label: 'MTD Collections', kind: 'money' },
  ...DENIAL_BUCKETS.map(bucket => ({
    key: `denial_${bucket.toLowerCase()}`,
    label: bucket,
    kind: 'count',
    // Five columns, one reason. Without this each bucket would fall back to its own key,
    // find nothing, and render a dash with nothing behind it. Still needed now they are
    // built: a practice whose payer sends no reason codes cannot be split by cause at all,
    // and one reason covers all five of its empty cells.
    reasonKey: 'denial_breakdown',
    group: 'Denial Breakdown',
  })),
];

// Today in the BROWSER's timezone, as YYYY-MM-DD. Deliberately not toISOString(), which
// converts to UTC and reintroduces the very off-by-one-day this exists to remove.
function localToday() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// `decimals` defaults to cents. 45+ A/R passes 0: it is a six-figure balance being scanned
// down a column for magnitude, where cents are noise, while Rev / Paid Claim and MTD
// Collections keep theirs because the cents are a meaningful part of a small figure.
function money(value, decimals = 2) {
  if (value === null || value === undefined) return <span className="mc-na">—</span>;
  return `$${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

function count(value) {
  if (value === null || value === undefined) return <span className="mc-na">—</span>;
  return Number(value).toLocaleString('en-US');
}

function cellValue(block, column) {
  // A column David drew that is not built. Renders the same dash as any other absence —
  // Aman's call, and the column grouping is what carries the distinction instead. The class
  // stays so the cell is still identifiable to a test and to the stylesheet.
  if (column.kind === 'pending') return <span className="mc-na mc-pending">—</span>;
  if (!block) return <span className="mc-na">—</span>;
  if (column.kind === 'ar') {
    // Absent from the payload for a period that has no as-of date, rather than zero.
    if (!(column.key in block)) return <span className="mc-na">n/a</span>;
    return money(block[column.key] && block[column.key].total, 0);
  }
  if (column.kind === 'days') {
    // Absent from the payload for a period with no as-of date, exactly like the A/R total it
    // describes — 'n/a' there rather than a dash. Present-but-null means the practice has
    // nothing aged, which is a real answer and still not a zero.
    if (!(column.key in block)) return <span className="mc-na">n/a</span>;
    const value = block[column.key];
    if (value === null || value === undefined) return <span className="mc-na">—</span>;
    // Whole days, no unit suffix — the column heading already says Days, and a tenth of a day
    // on a figure in the hundreds is false precision.
    return Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  if (column.kind === 'percent') {
    const value = block[column.key];
    // Null means no payer money moved in this period, which is not 0%. 0% would say every
    // dollar the practice received was keyed in by hand. The block carries the reason.
    if (value === null || value === undefined) return <span className="mc-na">—</span>;
    // One decimal, matching what the server rounds to, so 100 and 99.4 line up in the column.
    return `${Number(value).toFixed(1)}%`;
  }
  if (column.kind === 'money') return money(block[column.key]);
  if (column.kind === 'ratio') {
    const value = block[column.key];
    if (value === null || value === undefined) return <span className="mc-na">—</span>;
    // Shown as a PERCENTAGE of appointments that produced a claim: 0.86 reads 86%. The server
    // still sends a ratio, so this is presentation only. It can legitimately exceed 100% —
    // a practice raising more claims than it held visits, which is what a missed-visit fee
    // or a split claim does — and that is worth seeing rather than clamping away.
    return `${Math.round(Number(value) * 100)}%`;
  }
  return count(block[column.key]);
}

// Scoped to one practice when the app has one selected, and across every practice the user
// can see when reached from the admin route with none selected. Two reasons: it matches how
// every other page in Atlas behaves, and the single-practice endpoint reads one practice's
// claims where the grid reads every practice's, so the common case stops paying for the
// expensive one.
function MissionControlPage({ selectedPractice }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const practiceId = selectedPractice ? selectedPractice.id : null;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Send the reader's own date. Left off, the server falls back to its own clock, and
      // The server runs UTC — so from early evening in the US the grid reports tomorrow. Most
      // days that costs nothing, but on the last evening of a month "month to date" rolls
      // into the next one and collapses to a near-empty window. The route already permits
      // today+1 for exactly this. Same reasoning as todayInTimezone on the Appointments page.
      const base = practiceId
        ? `/api/practices/${practiceId}/mission-control`
        : '/api/mission-control';
      const url = `${base}?report_date=${localToday()}`;
      const response = await fetch(url);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Could not load Mission Control');
      }
      const body = await response.json();
      // The per-practice endpoint returns ONE row; normalise it to the grid's shape so
      // everything below renders from a single structure.
      setData(practiceId
        ? { report_date: body.report_date, rows: [body], excluded: [] }
        : body);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="mc-page"><p className="mc-muted">Loading…</p></div>;

  if (error) {
    return (
      <div className="mc-page">
        <div className="mc-error">
          {error}
          <button type="button" className="mc-retry" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  const rows = (data && data.rows) || [];
  // Every row shares a source-dependent definition set; the headings are page-level, so take
  // the first row's. With mixed sources on the grid the differences are noted in the text
  // itself ("this export carries no procedure codes"), so a heading tip stays truthful.
  const definitions = (rows[0] && rows[0].definitions) || {};
  // Every row shares the report date, so any row's window describes the active period. Shown
  // on the page rather than implied by the tab label: what "Current" covers is still an open
  // question with David, and a definition he disagrees with should be visible, not assumed.
  const activeWindow = rows.length ? rows[0].blocks[PERIOD].window : null;

  return (
    <div className="mc-page">
      <div className="mc-header">
        <div>
          <h1>Mission Control</h1>
          {/* "As at", not "Week ending". No block on this page measures a week ending today:
              Current is month to date and Last Week ends seven days ago, both stated under
              the tabs. A header claiming a week the columns do not measure is the same
              wrong-number-in-the-shape-of-a-right-one problem the service guards against. */}
          <p className="mc-muted">As at {data.report_date}</p>
        </div>
        {/* The grid as a CSV, for the same day the page is showing. Only on the grid: the
            export spans practices, so it is not the file to hand someone looking at one. */}
        {!practiceId && (
          <ExportButton
            path="/api/mission-control/export"
            label="Export grid CSV"
            getParams={() => new URLSearchParams({ report_date: data.report_date })}
          />
        )}
      </div>

      {activeWindow && <p className="mc-window">{activeWindow.label}</p>}

      <div className="mc-scroll">
        <table className="mc-table">
          <thead>
            <tr>
              <th className="mc-practice-col">Practice</th>
              {/* Each heading carries how its number is calculated. Definitions come from the
                  server because two of the four differ by source — new clients is counted by
                  procedure code or by first visit depending on what the export carries. */}
              {COLUMNS.map(c => {
                const how = definitions[c.key];
                return (
                  <th
                    key={c.key}
                    title={how || undefined}
                    className={c.kind === 'pending' ? 'mc-col-pending' : undefined}
                  >
                    {/* David's layout merges one "Denial Breakdown" heading across five
                        sub-columns. Repeating it small on each keeps that grouping legible
                        without a second header row, which every test here queries against. */}
                    {c.group && <span className="mc-col-group">{c.group}</span>}
                    {how ? <span className="mc-has-tip">{c.label}</span> : c.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              return (
                <React.Fragment key={row.practice.id}>
                  <tr className="mc-row">
                    <td className="mc-practice-col">
                      {row.practice.name}
                      <span className="mc-source">{row.practice.source}</span>
                    </td>
                    {COLUMNS.map(c => {
                      // A column can be empty for THIS period while being fine for another —
                      // an appointment feed that does not reach back across the window, say.
                      // The reason rides the cell so a dash is never just a dash.
                      const block = row.blocks[PERIOD];
                      // Two kinds of reason, period-specific first. A practice-level reason
                      // (no scheduling feed at all, no payer money at all, an export with no
                      // reason codes) used to render only in the expanded panel. That panel is
                      // gone, so the cell is now the ONLY surface for it — without this
                      // fallback those columns render a bare dash that reads as a zero, which
                      // is the exact failure the server's `unavailable` strings exist to
                      // prevent. The five denial buckets share one reason via reasonKey.
                      const reasonKey = c.reasonKey || c.key;
                      const why = (block && block.unavailable && block.unavailable[c.key])
                        || (row.unavailable && row.unavailable[reasonKey])
                        || undefined;
                      // A qualification on a number that IS shown, as opposed to a reason a
                      // number is missing. NO LONGER MARKED with an asterisk (David, too
                      // confusing) — the text still rides the cell as a tooltip, so hovering
                      // a figure that is understated still explains why, but nothing on the
                      // grid draws the eye to it.
                      // Fall back to reasonKey the same way `why` does: the five denial
                      // buckets share ONE server-side caveat keyed `denial_breakdown`,
                      // so keying only on c.key rendered it nowhere at all.
                      const caveat = block && block.caveats
                        && (block.caveats[c.key] || block.caveats[reasonKey]);
                      return (
                        <td key={c.key} title={why || caveat || undefined}>
                          {cellValue(block, c)}
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Which practice has which source and which features. Its own endpoint and its
          own failure mode: a matrix that cannot load must not take the grid with it. */}
      <CapabilityMatrix />

    </div>
  );
}

export default MissionControlPage;
